import { useState, useEffect, useCallback } from 'react';
import { openApiService } from '../Services/openApiService';
import { OpenApiConfig, OpenApiEndpoint } from '../Types';
import toast from 'react-hot-toast';
import useGlobals from './useGlobals';

export interface TestParameters {
    [key: string]: string | number | boolean;
}

export interface TestResult {
    success: boolean;
    endpoint: {
        identifier: string;
        method: string;
        path: string;
        name: string;
        fullUrl: string;
        description?: string;
    };
    request: {
        parameters: TestParameters;
        method: string;
        url: string;
    };
    response: {
        statusCode: number;
        headers: Record<string, any>;
        body: any;
        executionTime: number;
    };
    message: string;
}

const useOpenApiTesting = (resetSignal: boolean, setResetFlag: (value: boolean) => void) => {
    const { i18n } = useGlobals();

    // State
    const [configs, setConfigs] = useState<OpenApiConfig[]>([]);
    const [selectedConfig, setSelectedConfig] = useState<OpenApiConfig | null>(null);
    const [endpoints, setEndpoints] = useState<OpenApiEndpoint[]>([]);
    const [selectedEndpoint, setSelectedEndpoint] = useState<OpenApiEndpoint | null>(null);
    const [parameters, setParameters] = useState<TestParameters>({});
    const [testResult, setTestResult] = useState<TestResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [copied, setCopied] = useState<Record<string, boolean>>({});

    // Load configs on mount
    const loadConfigs = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await openApiService.getConfigs(false, 1, 100); // Get all configs
            setConfigs(response.data.data || []);
        } catch (error) {
            console.error('Failed to load configs:', error);
            toast.error(i18n.translate({ id: 'testing.error.loadConfigs', sourceName: 'OpenAPI' }));
        } finally {
            setIsLoading(false);
        }
    }, [i18n]);

    useEffect(() => {
        if (resetSignal) {
            clearResults();
            setSelectedConfig(null);
            setResetFlag(false);
        }
    }, [resetSignal, setResetFlag]);

    useEffect(() => {
        loadConfigs();
    }, [loadConfigs, resetSignal]);

    const loadConfigWithEndpoints = useCallback(
        async (configIdentifier: string) => {
            try {
                setIsLoading(true);
                const endpoints = await openApiService.getEndpoints(configIdentifier);
                const totalItems = endpoints.data.total;
                if (totalItems >= endpoints.data.data.length) {
                    const pages = Math.ceil(totalItems / endpoints.data.data.length);
                    let allEndpoints = endpoints.data.data;
                    for (let page = 2; page <= pages; page++) {
                        const pagedResponse = await openApiService.getEndpoints(
                            configIdentifier,
                            page,
                            endpoints.data.data.length,
                        );
                        allEndpoints = allEndpoints.concat(pagedResponse.data.data);
                    }
                    setEndpoints(allEndpoints);
                    setSelectedEndpoint(null);
                    setParameters({});
                    setTestResult(null);
                    return;
                }
            } catch (error) {
                console.error('Failed to load config with endpoints:', error);
                toast.error(i18n.translate({ id: 'testing.error.loadEndpoints', sourceName: 'OpenAPI' }));
                setEndpoints([]);
            } finally {
                setIsLoading(false);
            }
        },
        [i18n],
    );

    // Load endpoints when config changes
    useEffect(() => {
        if (selectedConfig) {
            // Load full config with endpoints
            loadConfigWithEndpoints(selectedConfig.identifier);
        } else {
            setEndpoints([]);
            setSelectedEndpoint(null);
            setParameters({});
            setTestResult(null);
        }
    }, [selectedConfig, loadConfigWithEndpoints]);

    // Reset parameters when endpoint changes
    useEffect(() => {
        if (selectedEndpoint) {
            // Initialize parameters based on endpoint definition
            const initialParams: TestParameters = {};
            (selectedEndpoint.parameters || []).forEach((param) => {
                if (param.schema?.enum && param.schema.enum.length === 1) {
                    initialParams[param.name] = param.schema.enum[0];
                } else if (param.required) {
                    initialParams[param.name] = param.type === 'number' ? 0 : '';
                }
            });
            setParameters(initialParams);
            setTestResult(null);
        } else {
            setParameters({});
            setTestResult(null);
        }
    }, [selectedEndpoint]);

    const handleConfigChange = (configIdentifier: string) => {
        const config = configs.find((c) => c.identifier === configIdentifier);
        setSelectedConfig(config || null);
    };

    const handleEndpointChange = (endpointIdentifier: string) => {
        const endpoint = endpoints.find((e) => e.identifier === endpointIdentifier);
        setSelectedEndpoint(endpoint || null);
    };

    const handleParameterChange = (paramName: string, value: string | number | boolean) => {
        setParameters((prev) => ({
            ...prev,
            [paramName]: value,
        }));
    };

    const executeTest = async () => {
        if (!selectedEndpoint) {
            toast.error(i18n.translate({ id: 'testing.error.noEndpoint', sourceName: 'OpenAPI' }));
            return;
        }

        const loadingToastId = toast.loading(i18n.translate({ id: 'testing.loading', sourceName: 'OpenAPI' }));

        try {
            setIsTesting(true);
            setTestResult(null);

            const response = await openApiService.testEndpoint(selectedEndpoint.identifier, {
                parameters: parameters,
            });

            // Map backend response to frontend format
            const backendData = response.data;
            const mappedResult: TestResult = {
                success: backendData.success,
                endpoint: backendData.endpoint,
                request: backendData.request,
                response: backendData.response
                    ? {
                          statusCode: backendData.response.statusCode,
                          headers: backendData.response.headers,
                          body: backendData.response.body,
                          executionTime: backendData.response.executionTimeMs, // Map executionTimeMs to executionTime
                      }
                    : {
                          statusCode: 0,
                          headers: {},
                          body: null,
                          executionTime: 0,
                      },
                message: backendData.message,
            };

            setTestResult(mappedResult);
            toast.dismiss(loadingToastId);
            if (response.data.success) {
                toast.success(i18n.translate({ id: 'testing.success', sourceName: 'OpenAPI' }));
            } else {
                toast.error(i18n.translate({ id: 'testing.error.failed', sourceName: 'OpenAPI' }));
            }
        } catch (error: any) {
            console.error('Test failed:', error);
            toast.error(
                error?.response?.data?.message ||
                    i18n.translate({ id: 'testing.error.execution', sourceName: 'OpenAPI' }),
            );
        } finally {
            setIsTesting(false);
        }
    };

    const clearResults = () => {
        setTestResult(null);
    };

    const copyToClipboard = (text: string, key: string) => {
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text);
            } else if (typeof document !== 'undefined') {
                // Fallback for environments without navigator.clipboard
                const el = document.createElement('textarea');
                el.value = text;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            }
        } catch (e) {
            console.error('Copy failed', e);
        }

        setCopied((prev) => ({ ...prev, [key]: true }));
        setTimeout(() => {
            setCopied((prev) => ({ ...prev, [key]: false }));
        }, 1500);
    };

    return {
        configs,
        selectedConfig,
        endpoints,
        selectedEndpoint,
        parameters,
        testResult,
        isLoading,
        isTesting,
        handleConfigChange,
        handleEndpointChange,
        handleParameterChange,
        executeTest,
        clearResults,
        loadConfigs,
        copied,
        copyToClipboard,
    };
};

export default useOpenApiTesting;
